import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface FacturaIntrare {
  id: string;
  numar: string;
  data: string;
  furnizor: string;
  descriere: string;
  suma: number;
  tva: number;
  total: number;
  parohieId?: string;
  status: 'neplatita' | 'platita' | 'anulata';
  dataScadenta: string;
  observatii?: string;
}

const FacturiIntrare: React.FC = () => {
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFurnizor, setFilterFurnizor] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState<FacturaIntrare | null>(null);

  // Mock data pentru facturi intrare
  const [facturiIntrare] = useState<FacturaIntrare[]>([
    {
      id: 'fi1',
      numar: 'FI-001/2024',
      data: '2024-01-15',
      furnizor: 'SC Materiale Constructii SRL',
      descriere: 'Materiale pentru renovare',
      suma: 5000,
      tva: 950,
      total: 5950,
      parohieId: 'parohia1',
      status: 'neplatita',
      dataScadenta: '2024-02-15',
      observatii: 'Factură urgentă'
    },
    {
      id: 'fi2',
      numar: 'FI-002/2024',
      data: '2024-01-20',
      furnizor: 'SC Utilitati Publice SA',
      descriere: 'Factură electricitate',
      suma: 800,
      tva: 152,
      total: 952,
      parohieId: 'parohia1',
      status: 'platita',
      dataScadenta: '2024-02-20'
    },
    {
      id: 'fi3',
      numar: 'FI-003/2024',
      data: '2024-01-25',
      furnizor: 'SC Libraria Religioasa SRL',
      descriere: 'Cărți religioase și spirituale',
      suma: 1200,
      tva: 228,
      total: 1428,
      parohieId: 'parohia1',
      status: 'neplatita',
      dataScadenta: '2024-02-25',
      observatii: 'Pentru biblioteca parohiei'
    },
    {
      id: 'fi4',
      numar: 'FI-004/2024',
      data: '2024-01-28',
      furnizor: 'SC Atelier Icoane SRL',
      descriere: 'Icoane religioase și obiecte de cult',
      suma: 1800,
      tva: 342,
      total: 2142,
      parohieId: 'parohia1',
      status: 'neplatita',
      dataScadenta: '2024-02-28',
      observatii: 'Icoane pentru altar și iconostas'
    }
  ]);

  const furnizori = Array.from(new Set(facturiIntrare.map(f => f.furnizor)));

  const filteredFacturi = facturiIntrare.filter(factura => {
    const matchesSearch = factura.numar.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         factura.furnizor.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         factura.descriere.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || factura.status === filterStatus;
    const matchesFurnizor = !filterFurnizor || factura.furnizor === filterFurnizor;
    
    return matchesSearch && matchesStatus && matchesFurnizor;
  });

  const totalNeplatite = facturiIntrare
    .filter(f => f.status === 'neplatita')
    .reduce((sum, f) => sum + f.total, 0);

  const totalPlatite = facturiIntrare
    .filter(f => f.status === 'platita')
    .reduce((sum, f) => sum + f.total, 0);

  const totalGeneral = facturiIntrare.reduce((sum, f) => sum + f.total, 0);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'platita': return 'success';
      case 'neplatita': return 'warning';
      case 'anulata': return 'error';
      default: return 'default';
    }
  };

  const handleViewFactura = (factura: FacturaIntrare) => {
    setSelectedFactura(factura);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedFactura(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Facturi Intrare
        </Typography>
        {hasPermission('gestiune.write') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {/* Implementare adăugare factură nouă */}}
          >
            Factură nouă
          </Button>
        )}
      </Box>

      {/* Statistici facturi */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  ⚠️
                </Typography>
                <Typography color="textSecondary">
                  Facturi neplătite
                </Typography>
              </Box>
              <Typography variant="h5" color="warning.main">
                €{totalNeplatite.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  ✅
                </Typography>
                <Typography color="textSecondary">
                  Facturi plătite
                </Typography>
              </Box>
              <Typography variant="h5" color="success.main">
                €{totalPlatite.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  💰
                </Typography>
                <Typography color="textSecondary">
                  Total general
                </Typography>
              </Box>
              <Typography variant="h5" color="primary.main">
                €{totalGeneral.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtre pentru facturi */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Caută facturi..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="">Toate</MenuItem>
                <MenuItem value="neplatita">Neplătită</MenuItem>
                <MenuItem value="platita">Plătită</MenuItem>
                <MenuItem value="anulata">Anulată</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Furnizor</InputLabel>
              <Select
                value={filterFurnizor}
                onChange={(e) => setFilterFurnizor(e.target.value)}
                label="Furnizor"
              >
                <MenuItem value="">Toți</MenuItem>
                {furnizori.map(furnizor => (
                  <MenuItem key={furnizor} value={furnizor}>{furnizor}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('');
                setFilterFurnizor('');
              }}
            >
              Resetează
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabel facturi */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Număr</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Furnizor</TableCell>
              <TableCell>Descriere</TableCell>
              <TableCell>Suma</TableCell>
              <TableCell>TVA</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Data scadență</TableCell>
              <TableCell>Acțiuni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFacturi.map((factura) => (
              <TableRow key={factura.id}>
                <TableCell>{factura.numar}</TableCell>
                <TableCell>{factura.data}</TableCell>
                <TableCell>{factura.furnizor}</TableCell>
                <TableCell>{factura.descriere}</TableCell>
                <TableCell>€{factura.suma.toLocaleString()}</TableCell>
                <TableCell>€{factura.tva.toLocaleString()}</TableCell>
                <TableCell>
                  <Typography fontWeight="bold">
                    €{factura.total.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={factura.status}
                    color={getStatusColor(factura.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>{factura.dataScadenta}</TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => handleViewFactura(factura)}
                    color="primary"
                  >
                    <ViewIcon />
                  </IconButton>
                  {hasPermission('gestiune.write') && (
                    <IconButton color="secondary">
                      <EditIcon />
                    </IconButton>
                  )}
                  <IconButton color="default">
                    <DownloadIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog pentru vizualizare factură */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalii factură intrare
        </DialogTitle>
        <DialogContent>
          {selectedFactura && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Număr factură
                </Typography>
                <Typography variant="body1">
                  {selectedFactura.numar}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Data
                </Typography>
                <Typography variant="body1">
                  {selectedFactura.data}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Furnizor
                </Typography>
                <Typography variant="body1">
                  {selectedFactura.furnizor}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  label={selectedFactura.status}
                  color={getStatusColor(selectedFactura.status) as any}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Descriere
                </Typography>
                <Typography variant="body1">
                  {selectedFactura.descriere}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="textSecondary">
                  Suma fără TVA
                </Typography>
                <Typography variant="h6">
                  €{selectedFactura.suma.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="textSecondary">
                  TVA
                </Typography>
                <Typography variant="h6">
                  €{selectedFactura.tva.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="textSecondary">
                  Total
                </Typography>
                <Typography variant="h5" color="primary.main">
                  €{selectedFactura.total.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Data scadență
                </Typography>
                <Typography variant="body1">
                  {selectedFactura.dataScadenta}
                </Typography>
              </Grid>
              {selectedFactura.observatii && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Observații
                  </Typography>
                  <Typography variant="body1">
                    {selectedFactura.observatii}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Închide</Button>
          <Button variant="contained" startIcon={<DownloadIcon />}>
            Descarcă
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FacturiIntrare;
